// Help centre articles. Each `## Heading` is a section a "?" link can open on
// its own (its id is the heading slugified, e.g. "## Hard sets per muscle" ->
// "hard-sets-per-muscle"), so keep headings stable once something links to them.
//
// Write them to be taken in at a glance (#162, and app/CLAUDE.md):
//   - Lead with the one thing to do or know, in one sentence.
//   - Numbered steps for how-tos, bullets for what's shown. About 40 words a
//     paragraph and five bullets a list at most (articles.test.js checks).
//   - A screenshot per section where it helps: ![what it shows](/help/name.webp),
//     generated by app/scripts/help-shots.js, never hand-captured.
//   - Examples as a small table, not a run-on sentence.
//   - The "why" and the edge cases under a fold:
//       +++ More detail
//       …
//       +++
//   - Button names in bold, exactly as they appear. Update the article in the
//     same change as the screen.

export const HELP_AREAS = [
  { id: "start", title: "Getting started" },
  { id: "sessions", title: "Sessions" },
  { id: "coach", title: "The Coach" },
  { id: "programmes", title: "Programmes" },
  { id: "activity", title: "Activity and progress" },
  { id: "social", title: "Training with mates" },
  { id: "devices", title: "Devices and heart rate" },
  { id: "account", title: "AI, account and the app" },
];

export const HELP_ARTICLES = [
  {
    id: "how-khronos-works",
    area: "start",
    title: "How Khronos works",
    summary: "Build a session, run it with the Coach, and see your week add up.",
    related: ["getting-started", "todays-suggestion", "coach-basics"],
    body: `
Khronos builds a workout around you, coaches you through it, and tracks how your week adds up. For a quick tour, tap **Show me around** at the top of Help.

![The Session tab, with today's suggestion at the top](/help/session-home.webp)

## The three tabs you'll use most
- **Session:** where a workout starts. Press **Start** on today's suggestion.
- **Coach:** runs the workout, step by step, with sounds.
- **Activity:** your last 7 days against your targets, and your history.

**Programme** follows a multi-week plan. **Social** is for training with mates.

## What Khronos uses to plan
Your goal, your time, your kit, your level and any injuries. It also looks at your recent training, so it balances your week instead of repeating yesterday.

## Signed in or not
- **Not signed in:** everything stays on this device.
- **Signed in with Google:** your profile, history and programmes sync across devices.

## Basic and Advanced
New to Khronos? You start on **Basic**, which keeps the options most people need. Turn on **Advanced options** in the menu under your profile picture to see everything.

+++ More detail
Hidden options keep working at whatever they're set to. To see them once without switching, tap **More options** at the bottom of a screen.
+++
`,
  },
  {
    id: "getting-started",
    area: "start",
    title: "Setting up Khronos",
    summary: "The four quick questions, and how to change your answers later.",
    related: ["weekly-targets", "build-a-session"],
    body: `
Answer four quick questions and Khronos plans around you. You can skip any of them.

## What it asks
1. **What you're training for:** sets the balance of lifting, intervals and cardio.
2. **How much time you have:** days a week and minutes a session.
3. **Where you train:** Khronos only plans exercises your kit allows.
4. **About you:** experience, year of birth and injuries. All optional.

At the end, choose **Keep it simple** or **Show me everything**.

## Changing your answers
- Change one answer in **Profile**.
- Or run it again: **Run getting started again** at the bottom of Profile.
`,
  },
  {
    id: "todays-suggestion",
    area: "sessions",
    title: "Today's suggestion",
    summary: "Why the Session tab suggests what it does, and how to change it.",
    related: ["weekly-targets", "build-a-session", "programmes"],
    body: `
The card at the top of the Session tab is one suggestion for today. Press **Start** to build and run it.

## Where it comes from
- **On a programme:** your next programme session.
- **Otherwise:** **What's next**, the session that fills the biggest gap in your week. The line under the title says why.
- **New to Khronos:** **Surprise me**, a session for your goal, picked for variety.

## What's next and Surprise me
- **What's next** plans from what your week needs.
- **Surprise me** mixes things up, still leaning towards your goal.

Both show the session's shape (say "Strength + metcon finisher") before it's built.

## Start, shuffle and customise
- **Start:** builds it and opens it.
- **Shuffle:** shows something else.
- **Sliders:** opens the full build screen with this filled in.
`,
  },
  {
    id: "build-a-session",
    area: "sessions",
    title: "Building a session",
    summary: "The build screen: goal, time, energy, kit, notes and recipes.",
    related: ["todays-suggestion", "saved-sessions", "ai-options"],
    body: `
Tap **Build your own** on the Session tab, choose what you want, then **Generate session**.

![The build screen: Train for, Time and Energy](/help/build-session.webp)

## Train for
What the session is for: strength, hypertrophy, hard intervals, cardio and more. Or let Khronos choose with **What's next** or **Surprise me**.

## Time and energy
- **Time:** drag sideways, tap **+** and **−**, or tap the number and type it.
- **Energy:** how you feel today. On a low day it eases off.

## Kit and notes
- **Kit:** where you are and what's there.
- **Notes:** anything for today, like "no jumping".

## Format
How the main work runs: **Auto**, straight sets, a circuit, EMOM, AMRAP or Tabata. In Basic, it's under **More options**.

## No faff
Fewer kit changes and no setup-heavy exercises, for when you want to get in and out. In Basic, it's under **More options**.

## Recipes
Save your current choices as a recipe and reuse them in one tap. Recipes show on the build screen.

## Setups between blocks
A **Setup** line between two blocks is time to move and set up your kit. Khronos puts them in for you.
- Tap one to change its length or skip it.
- Move, add or delete a block and the setups follow.
- A skipped one shows as **Setup skipped · Add back**.

+++ More detail
The usual length comes from Settings, or from the place you train at. Sessions you build step by step have no setups.
+++
`,
  },
  {
    id: "saved-sessions",
    area: "sessions",
    title: "Saving, repeating and sharing",
    summary: "Run a session again, keep it for later, or send it to someone.",
    related: ["activity", "build-a-session"],
    body: `
Any session can be run again, saved, or sent to someone.

## Repeat a recent session
- Your two latest sessions show under **Repeat a recent session** on the Session tab.
- Any session in Activity: open it and tap **Run again**.

## Saved sessions
**Saved sessions** on the Session tab holds yours and ones shared with you. To save one you've built, tap **⋯** beside its title, then **Save to your sessions**.

## The ⋯ menu on a built session
Save, **Regenerate**, share a link, copy, or **Clear session**. **Send to Coach** at the bottom runs it.

## Sharing
From a session's **⋯** menu:

| Choose | You get |
|---|---|
| **Copy summary** | a readable list to paste in a message |
| **Copy session plan** | the plan, which pastes into Khronos |
| **Share** | a link to a copy |

Turn on **Hide times** to leave your own times out.
`,
  },
  {
    id: "coach-basics",
    area: "coach",
    title: "Using the Coach",
    summary: "Running a session: the ring, Done, weights, fixing the plan, a finish-by time and finishing.",
    related: ["effort", "timers", "devices"],
    body: `
Tap the ring to start, pause or resume. Timed steps count down by themselves; on a reps step, press **Done** when you've finished.

![The Coach running a session](/help/coach-running.webp)

## Before a session
With nothing loaded, the Coach shows a **ready check**: strap, bike or rower, Khronos Timer and sound. Green is ready; tap a dashed tile to fix it.

## Starting
1. Send a session to the Coach, or press **Start** on today's suggestion.
2. Tap the ring.

The plan below shows what's coming. **Clear session** unloads it.

## The ring
- **Outer ring:** your place in the whole session.
- **Inner ring:** the step you're on.
- **The strip at the top:** the session's blocks. Tap one to preview it.

## Timed steps and reps steps
- **Timed:** the clock counts down by itself.
- **Reps:** do your reps, then press the green **Done**.
- **Linked steps:** shown as one, like **Deadlift + Row + Clean**. One **Done** finishes them all.

## Weight
On a reps step, tap **Add weight** under the ring to record the load. Tap a weight to change it.

## Reps you actually did
**Done** records the planned reps. Did more or fewer? In the rest after it, tap the set under the ring, like **Push · set 1: 12 reps**, and change it.

+++ More detail
Sets with no rest after them can be fixed at the end: the finish screen lists every set under **Reps done**. Your programme uses these numbers to plan next week's loads.
+++

## Fixing the plan mid-workout
![Paused, with Edit beside Session plan](/help/coach-paused.webp)

1. Pause, then tap **Edit** beside Session plan.
2. Swipe a step right to edit it, or left to delete it.
3. Tap **Apply**. Your Khronos Timer follows too.

+++ More detail
Finished steps are greyed out and stay as recorded. Change the step you're on and the time already done still counts. A Live session with training partners can't be edited.
+++

## Finishing by a set time
1. On the start screen, tap **Set a finish-by time**.
2. Pick when you must be done. Turn on **Alarm at this time** if you want a sound.

If you fall behind, Khronos offers one trim at your next rest. Tap **Trim** or **Keep going**.

+++ More detail
It works out your finish time from the pace you're actually going, and offers a trim once you're more than a minute over. It trims in this order, never mid-set:

1. Shorter rests, by up to 30%.
2. A set or two off blocks still to come, the last one first, never below half.
3. Whole blocks still to come, the last one first, cool-down included.

It asks again only if you fall another two minutes behind. With the app in the background you get a notification, if you've allowed them.
+++

## A longer warm-up in the cold
With a Khronos Timer running your session, a first warm-up reading under 10 °C brings an offer of more warm-up: 3 minutes, or 5 at 0 °C or below. Tap **Add** or **Not today**.

## Finishing or stopping early
- At the end, rate how hard it felt (see Effort).
- To stop early, pause and press **Stop workout**. Save what you did or discard it.
- Want more? **Keep going** adds a Free training block to the end and starts it. It runs on the phone, the Khronos Timer or a live share, as the session did. Log what you do, then finish and save once.

`,
  },
  {
    id: "effort",
    area: "coach",
    title: "Effort (RPE)",
    summary: "What the 1 to 10 effort score means and how Khronos uses it.",
    related: ["coach-basics", "activity"],
    body: `
After a session, rate how hard it was from 1 to 10. This is your rating of perceived exertion (RPE).

## The scale
| Score | Feels like |
|---|---|
| **1–3** | easy, you could chat the whole time |
| **4–6** | working, but comfortable |
| **7–8** | hard, a little left in you |
| **9–10** | maximal, nothing left |

## How it's used
Your scores show in Activity and help Khronos judge the next session. A run of 9s and 10s is a sign to ease off.
`,
  },
  {
    id: "timers",
    area: "coach",
    title: "Timers",
    summary: "Free training with a log as you go, and Tabata, HIIT, EMOM, AMRAP, stopwatch and countdown timers.",
    related: ["coach-basics"],
    body: `
Pick a timer, change it if you like, and press **Start**. Timers are on the Coach (with nothing loaded) and under **More ways to start** on the Session tab.

## Free training
Start the clock and log what you do as you go, with or without a plan.

![Free training with a set and a row logged](/help/free-training.webp)

1. Tap **Just train** on the Session tab, then the ring.
2. Type in the box at the bottom, numbers and all, and press Enter.
3. Tap **Same again** for another set.

| Type this | You get |
|---|---|
| bench 80x8 | one set |
| bench 3x8 80 | three sets |
| pull-up x10 | a bodyweight set |
| plank 3x45s | three timed holds |
| row 2k, bike 10min | cardio |
| tabata burpees 8 rounds | a block |

+++ More detail
- **Names:** Enter keeps your words. Tap a suggestion to use its name.
- **Fix an entry:** tap its name, or a set's number.
- **Rest:** tap the hourglass. **Open** counts up; a length counts down with beeps.
- **A paired rower or bike** logs each piece itself when you stop, marked **From rower**.
- **Finishing:** pick what you did, tidy the log, and it's saved.
+++

## Checklist
Jot down what you'll do before you start, then tick it off by logging.

1. Before starting, tap **Add a checklist**.
2. Type it like the log, or start from **Last time's exercises** or **A saved session**.
3. Once going, tap a row to log it with its numbers filled in.

Anything left over shows when you finish.

## The timers
| Timer | What it does |
|---|---|
| **Tabata** | 20 s hard, 10 s rest, 8 rounds |
| **HIIT** | your own work and rest |
| **EMOM** | work at the top of every minute |
| **AMRAP** | as many rounds as you can in a set time |
| **Stopwatch** / **Countdown** | counts up / counts down |

## Your own timers
Change a timer and tap **Save as my timer**. It shows under **Your timers**, most recent first.
`,
  },
  {
    id: "programmes",
    area: "programmes",
    title: "Programmes",
    summary: "Multi-week plans: setting one up or importing your own, building each session, deloads and check-ins.",
    related: ["todays-suggestion", "weekly-targets"],
    body: `
A programme is a plan over several weeks towards one goal. Khronos lays out every week, then builds each session as you reach it.

![A programme, with the next session at the top](/help/programme.webp)

## Creating one
1. On the Programme tab, choose a goal, how long, how often and your time per session.
2. Tap **When** to pick your days and start date.
3. Check the preview, then create it.

## Importing a programme you already have
Got a programme from a coach, like a 12-week spreadsheet? Import it and Khronos runs it exactly as written. It never changes or re-plans those sessions.

1. Tap **⋯** next to the programme name, then **My programmes** › **Import a programme**. With no programme yet, tap **Already have a programme? Import it**.
2. Choose the spreadsheet (Excel or CSV). Khronos reads each week's tab on your phone.
3. Check the weeks. Tap a session to see its exercises. **Session** or **Note** chooses whether it runs as a session or shows as an "Also today" note, which suits club training or a mobility reminder.
4. Pick the week you're up to, then **Save programme**. Earlier weeks are kept but skipped.

+++ More detail
- **Your layout or ours:** a sheet with a header row (Exercise, Sets, Reps / time, Load, Rest) and a row for each day reads straight in. Anything else, or text copied from a PDF with **Paste text**, is read by AI with your AI settings, one request per tab. Or download the **Khronos template** and fill it in.
- **Roles:** if some sessions have extra exercises for one role (a sweep or a drummer, say), pick yours. The rest stay as written.
- **What's left out:** your own logged weights and notes in the sheet, and tabs that aren't the programme, such as guides and logs.
- **Training together** works too: mates who join get the same weeks, on the same calendar.
+++

## Training days
Weeks run Monday to Sunday. Khronos spreads your sessions evenly; tap **When**, then the days along the top, to choose your own.

+++ More detail
The hardest sessions go on days with the most rest around them, and a long day goes on the weekend if you train then. Changing **How often** goes back to an even spread.
+++

## Starting date
- **Start today:** days already gone this week are skipped, so week 1 is short.
- **Start next Monday:** a full first week. Suggested from Friday on.
- **Pick a date:** any day. Earlier days show as missed unless you log them.

## Building and starting sessions
The **Next** card at the top is your next session. **Start** builds it if needed, then runs it.

![A built session: Start, Edit, Rebuild, and Share at the bottom](/help/programme-session.webp)

- Tap any session in the weeks to view it, build it, edit it or rebuild it.
- At the bottom, **Copy summary** or **Share a link** sends it to a friend.

A session not built yet can also be filled with **Generate with AI**, **Write manually** ("squat 5x5 100", "row 2k") or **Use a saved session**.

## Changing your week
Plans change. Sessions you haven't done can move within their week.

- **Not feeling it today?** Tap **Change** on the Next card. Pick another of this week's sessions, or build something else. Today's session goes into the week's pool.
- **Busy or sick?** Open the week and tap **Plan this week**. Tap a session to put it on any day, back in the pool, or skip it.
- **Trained off the programme?** Tap the session, then **Move, pool or skip** › **I've done it already**, and pick it from your history.

+++ More detail
Two sessions can share a day. Skipped sessions don't count as missed. Anything still in the pool at the end of Sunday counts as missed, like any unfinished day.
+++

## Equipment
Sessions are built for your Profile's equipment unless you choose otherwise.

1. Tap **⋯** next to the programme name, then **Equipment**.
2. Pick one of your equipment sets.

Every session built for the programme after that uses only that kit.

## Training together
Do a programme with mates: **⋯** > **Train together**, then send the link.

- Each mate gets their own copy, on their own days.
- Whoever builds a session first shares it. The others see **Sam built it**, then **Use it** or **Adapt for me** (swaps anything their kit can't do).
- Ticks on each session show which mates have done it.

+++ More detail
A mate's copy follows yours, including your changes and the sessions you build, until they tap **Adapt for me** or change the programme themselves. After that it's theirs, and only the "built it" offers still come through. **Share a copy** in the same menu sends a one-off copy instead.
+++

## What goes into each week
Your goal sets the week's strength, cardio and interval work, the same targets Activity measures. Khronos fits them into your days and spreads the hard ones out.

+++ More detail
Cardio comes in four kinds:

- **Zone 2:** easy and steady, a pace you could chat at. Most of your cardio.
- **VO2max:** hard 3 to 4 minute intervals. One a week when you have two or more cardio days.
- **Zone 2 + Sprints:** Zone 2 ending in a few all-out sprints.
- **Sprint Intervals:** a whole session of all-out efforts, when there's room.

Heavy days get your full time, lighter days a little less, interval days about half an hour. When your goal needs one more session than you have days, a moderate strength day ends with a short sprint finisher.
+++

## Long day
With Zone 2 days in your plan, you can add one longer Zone 2 session a week. It goes on the last Zone 2 day; drag it to move it.

## Checking the plan against your notes
Write anything under **Goals and limits** (a race, a week away, a sore knee), then tap **Check the plan against my notes**. Khronos suggests a few changes with the reason for each.

+++ More detail
It can shorten a day, swap two days, change a kind of cardio, move a deload by up to 2 weeks, or add a note to a day. Anything that breaks its own rules is left out. Tap the cross to drop a change, or **Check again** after editing your notes.
+++

## Structures
All of them do a little more over time. **Auto** picks one for you.

- **Alternating:** heavy and moderate days take turns.
- **Daily Undulating:** a strength, a hypertrophy and a power day each week.
- **Block:** the whole programme drives one adaptation.
- **Linear:** the same lifts with a little more each week. Best for newer lifters.

## Deload weeks
Programmes of 6 weeks or more end on a lighter **deload** week, and never run more than 6 hard weeks in a row. It's meant to feel easy.

## Missed sessions
Missed sessions stay in the record. The programme carries on from where you are; there's nothing to catch up.

## Repeat last week
Ill or away? **Repeat last week** in the **⋯** menu puts last week's missed sessions into this week and moves the rest back a week. It comes with **Undo**.

## Weekly check-in
After a light week, a card asks what happened:

- **I was ill:** this week starts easier, then repeats last week.
- **I was travelling** or **Busy or tired:** this week repeats last week.
- **It was fine:** nothing changes.

+++ More detail
A light week is fewer than half your sessions, or much less time than planned.

- If a deload is due soon, you can count the light week as the deload.
- Two light weeks in a row ease the next one in.
- If most sessions felt 9 or 10 for two weeks, it offers to bring the deload forward.
- Anything that moves a deload shows **Undo**.
+++
`,
  },
  {
    id: "activity",
    area: "activity",
    title: "The Activity tab",
    summary: "Your last 7 days, weeks on target, progress and your full history.",
    related: ["progress", "weekly-targets", "logging", "effort"],
    body: `
Activity shows how your week is going and everything you've done.

![Activity: the last 7 days against your targets](/help/activity.webp)

## Last 7 days
The days you trained against your target, then your strength, hard interval and cardio sessions against your weekly targets. Below it: hard sets per muscle, lowest first, with the 10 to 20 range shaded.

To change what a session counts as:
1. Tap the session, then **⋯** › **Edit**.
2. Under **Counts as**, tap **Strength**, **Hard intervals** or **Cardio**.
3. Tap **Save changes**.

## Weeks on target
Weeks in a row (Monday to Sunday) you've trained on at least your target days. Rest days never break it, and this week can't end it.

## Progress
The **Progress** card shows your fitness, VO2max and FTP. Tap **See progress** for the charts behind them: training by week, VO2max, your power curve, FTP and efficiency.

## Your history
Sessions grouped by week. Tap one for its details. **Run again** loads it; **Share** (the arrow beside it) sends it to someone; **⋯** has edit and delete.

## Share a workout
Send a finished workout as a picture, a message or a link.

1. Tap the session in Activity, then **Share** next to **Run again**.
2. Pick what to send: **Picture**, **Message** or **Link**.
3. Tap **Share**, then pick WhatsApp or any app. **Copy** puts it on your clipboard instead.

| Send | What your mate gets |
| --- | --- |
| Picture | A card with your numbers and a small graph, dark or light |
| Message | **Short:** a few lines for WhatsApp. **Full:** every step with your times |
| Link | The session, to look at and run themselves |

+++ More detail
- **Your numbers:** under the picture or short message, tap up to six numbers from the rows (Workout, Heart, Power…). Your pick is kept for next time.
- **The graph:** heart rate if you wore a strap, else power from a bike or rower, else your steps as blocks, work in red and rest in grey. With a strap and intervals, **Graph** picks heart rate, the intervals or both.
- **Link to this workout** (under a message) adds the session's link. Without it the message links the Khronos site.
- **Links** open without an account. Your mate signs in to save or run it.
- **No share sheet** (some computers): the picture saves as a file, and messages and links go on your clipboard.
- **Android app:** pictures need app version 0.6.0 or later.
+++

## Heart rate and power charts
A session with a heart rate strap, bike or rower has a **Telemetry** tab.

- **Steps:** the strip under the chart shows each step: red for work, blue for rest, grey while paused. Tap one for its time and average heart rate and power.
- **Lines:** tap **HR**, **Power** or **Cadence** to hide or show that line.
- **Zoom:** pinch (or scroll on a computer), then drag to move along. **Reset zoom** goes back.

+++ More detail
Heart rate keeps recording while you're paused. Sessions recorded before 22 October 2026 didn't note when each step started: their steps are laid end to end and marked "Steps placed approximately". If a pause would throw them out, there's no strip.
+++

## Editing a past session
Tap **⋯** › **Edit** to fix the name, time, length or steps.

+++ More detail
On a step or block, **⋯** has more:

- **Split into rounds:** turns "Set × 3" into its three rounds, each named for what you did, with your times kept.
- **Ungroup:** removes a block that runs once and keeps its steps.
+++

## Searching
Search finds sessions by name, notes or exercise. The date filter narrows to 30 days, 3 months or 12 months, and the totals follow.
`,
  },
  {
    id: "progress",
    area: "activity",
    title: "Progress",
    summary: "Training load and fitness, VO2max, power curve, FTP and efficiency over time.",
    related: ["activity", "effort"],
    body: `
Progress shows how your training is adding up. Open it from the **Progress** card on Activity. Each part appears once you have sessions for it.

## Training by week
Minutes trained each week for the last 8 weeks, split into strength, intervals and cardio. This week is drawn lighter while it's still going. Tap a bar for its figures.

Once most of your sessions have an effort rating or heart rate, a **Load** switch appears. It adds up each session's training load:

- **With heart rate:** measured from time in each zone. A minute in Zone 3 counts three times a minute in Zone 1.
- **Without heart rate, or lifting:** effort (1 to 10) times minutes. A 45 minute session at 7 is 315.

On **Load**, each bar is split by heart rate: easy (Zones 1 to 2), threshold (3 to 4) and VO2max (5). Load from effort alone is grey. A sentence underneath gives your easy share; polarised training aims for about 80%.

The white line is your **fitness**: your load over about the last 6 weeks, shown as a typical week. Next to it, **form** compares fitness with the last week's load:

| Form | Means |
|---|---|
| Fresh | Rested, fitness to spare |
| Neutral | Training about as usual |
| Productive | Building fitness |
| Overreaching | A hard block; normal for a while |
| Detraining | Resting long enough to lose fitness |

+++ More detail
Heart-rate load is Edwards' TRIMP: minutes in Zones 1 to 5, weighted 1 to 5. It's put on the same scale as effort load using your sessions that have both, so the two add up. Until you have three of those, one zone point counts as two.

Strength sessions always use effort, because heart rate under-reads lifting.

Fitness and fatigue are the 42-day and 7-day weighted averages TrainingPeaks uses. Form is fitness minus fatigue as a share of fitness, on intervals.icu's bands. The line shows after 4 weeks of sessions.

Load waits for most sessions to have a figure, because a week with none would look like a rest week. Those weeks are also left out of the "last week" comparison. It's a guide to your trend, not a warning: Khronos doesn't change your plan because of it.
+++

## VO2max
Your VO2max from steady bike rides, your air bike or your 2k row. Tap it for every estimate and where it came from. Add a 2k in Profile, under **Your numbers**.

## Power curve
Your best average power for 5 seconds up to an hour, on the bike and rower:

- **Solid line:** your best in the last 8 weeks.
- **Dashed line:** your best ever.
- **Red dot:** a best set in the last 2 weeks.

It needs a paired machine that shows power. Durations are spread out so short sprints and long efforts both fit.

## FTP
FTP is the power you could hold for about an hour, worked out as 95% of your best 20 minutes on the bike in the last 90 days. Each ride then gets two figures:

| Figure | Means |
|---|---|
| IF | How hard the ride was against your FTP. 0.75 is steady, 1.0 is flat out for an hour |
| TSS | How much training the ride was. An hour at FTP is 100 |

+++ More detail
IF and TSS use normalised power, which weights surges more than plain average power, because hard efforts cost more than their average suggests. These are TrainingPeaks' measures, so they compare with what other apps show.
+++

## Efficiency and recovery
How your fitness is changing, session by session:

- **Watts per beat:** power for each heartbeat on steady rides. It rises as you get fitter.
- **Aerobic drift:** how much that fell from the first half of a steady ride to the second. Under 5% is a solid aerobic base.
- **Heart-rate recovery:** how far your heart rate drops in the minute after a hard interval. A bigger drop usually means better fitness.

+++ More detail
Watts per beat and drift only use steady bike efforts of 20 minutes or more, so they compare like with like. Recovery counts work steps of 30 seconds or more that end in Zone 4 or 5 and are followed by at least a minute of rest.
+++
`,
  },
  {
    id: "weekly-targets",
    area: "activity",
    title: "Weekly targets",
    summary: "How your goal and days a week set your strength, interval and cardio targets.",
    related: ["activity", "getting-started", "todays-suggestion"],
    body: `
Your weekly targets are how many strength, hard interval and cardio sessions make a good week. What's next and Activity both aim for them.

## Where they come from
- **Your goal** sets the balance: building muscle leans on lifting, endurance on easy cardio.
- **Your days a week** set how many fit.

One session can count twice, like strength with an interval finisher.

## Days a week
Set it in **Profile › Days a week**, or with **Change** on the Activity card.

+++ More detail
Left on **Auto**, Khronos uses your programme's schedule while one runs, otherwise how often you've trained recently, otherwise 3.
+++

## Hard sets per muscle
A hard set is one taken close to failure. Aim for about 10 a week per major muscle; up to about 20 if muscle is your goal. Only sessions done in Khronos add sets.

## From 65
WHO advises strength and balance work on 3 or more days a week from 65. Khronos suggests it; nothing changes unless you tap **Raise to 3**.
`,
  },
  {
    id: "logging",
    area: "activity",
    title: "Logging workouts from elsewhere",
    summary: "Add a run, a class or a Garmin activity to your history.",
    related: ["activity", "weekly-targets"],
    body: `
Workouts done away from Khronos still count towards your week.

## Log a workout
1. Tap **Log a workout you've done** on the Session tab, or **Add › Log a workout** on Activity.
2. Describe it in your own words, like "5 km run, felt easy".
3. Check the details Khronos fills in, then save.

## Import from Garmin
**Add › Import from Garmin** takes .fit files or a full Garmin export. Anything already imported is skipped.

## Fixing a logged session
Open it in Activity and tap **Edit**.
`,
  },
  {
    id: "social",
    area: "social",
    title: "Mates, groups and live sessions",
    summary: "Share workouts with mates, run a group, and train together live.",
    related: ["khronos-device", "account-sync"],
    body: `
**Social** needs an account. It has three tabs: **Feed**, **Mates** and **Groups**.

## The feed
Workouts you finish post themselves. **Your workouts post to** sets who sees them: **Mates**, **Everyone** or **Nobody**.

- **Post:** write one by hand, to mates or a group.
- **On a post:** like, comment, **View** the plan, or **I was there**.
- **Take yours down:** tap the bin. The session stays in your history.

## Mates
- **Find mates:** search people on Khronos by name or email, then tap **Add**.
- **Invite by link:** for anyone else. They sign in and you're mates.
- **This week:** you and your mates by sessions and minutes since Monday.

Tap a mate to see their streak and badges, or to remove them.

## Training partners
Pick one mate to keep each other on track.

1. On **Mates**, tap **Choose a training partner**.
2. Tap **Ask** next to a mate. They tap **Accept**.

A week counts when you both hit your own target. The streak is weeks in a row.

+++ More detail
- **Targets:** you each train to your own days a week. Changes apply from next Monday.
- **Freezes:** one a month covers a missed week automatically.
- **Nudges:** if either of you is behind on Friday, you both get a note.
- **Badges** at 4, 12, 26 and 52 weeks.
- **One partner at a time.** Tap the card to end it.
+++

## Badges
Nearly 300 badges for sessions, streaks, distance, power, heart-rate zones, time of day, mates and programmes. Training from before badges existed counts too.

- **Ranks:** bronze, silver, gold, platinum, diamond.
- **Where:** the **Badges** card on **Activity**. **Next up** shows the three closest. Mates shows your mates' badges.
- **When you earn one:** a sheet shows it just after you save the session, and the session lists it under **Badges earned**.
- **One medal per kind:** your highest tier, with a number for how many tiers you hold. Tap it for every tier and its date.
- **New ones** carry a white star until you open your badges. Ones earned another way arrive as a message; tap **See them**.
- **Not for you?** Turn them off in **Settings › Mates & apps › Badges**.

+++ More detail
Streak badges count the streak you're on, and "in a week" or "in a month" badges count this week or month. Some are secret until earned. Your mates can see your badges. They're still counted while turned off.
+++

## Challenges
Opt-in goals with a limited-edition badge, on **Activity** under **Badges**.

1. Tap **Take on a challenge**.
2. Tap **Join** next to one, or **Challenge a mate**.

- **Khronos challenges:** new each month, like rowing 42.2 km in November. Every session in its dates counts, even from before you joined.
- **The white mark** on the bar is where you'd be today to finish on time.
- **Challenge a mate:** you both hit your own weekly target, every week, for 4 weeks (or pick 1 to 12). Miss a week and it ends.

+++ More detail
- Targets are locked in on Monday, as for training partners.
- A mate challenge starts the Monday after they accept.
- With badges turned off, challenges are hidden too.
+++

## Your level
Your level is how many weeks you've hit your own weekly target, counted from your first session. It's on **Profile**, in a shield.

- **The ticks** are this week's days against your target.
- **Hit your target** and the week counts on Monday: your level goes up by one.
- **It never goes down.** Each week is judged on the target you had then.
- **Mates** see your level beside your name, if you both have badges on.

## Rare efforts
When a row or ride stands out against your own last 12 months on the same machine, the session says so in one line, such as **Best 2 km this year: 7:41.8**.

- **Where:** a message just after you save, the session in your history, and its Summary.
- **What counts:** the rower's fastest 500 m, 2 km and 5 km, and best 1, 5 and 20 minutes of power on the rower or bike.

+++ More detail
- With under 5 efforts it only marks your first.
- From 5 it says where this one sits, like **2nd best of your last 14**, for your top 3.
- From 30 it gives a percentage, for your top 10%.
- Only the most notable line shows. It's yours alone, so it shows with badges off too.
+++

## Mates' board
A weekly board on **Activity** for you and mates who switch it on. Off until you do.

1. Go to **Settings › Badges**.
2. Turn on **Show me on my mates' board**.

It appears once a mate has it on too, and resets every Monday.

- **Sessions:** days trained as a share of your own weekly target, up to 150%.
- **2 km row, 500 m row, 20 min ride:** your best this week against your usual. Your usual is the middle of your last 5 on the same machine in 90 days.

+++ More detail
- Nobody is ranked on raw speed or watts, so a beginner can top the board.
- You need 3 earlier efforts on a machine before your usual is set.
- Rower times are worked out from the watts the rower reports.
- With badges turned off, you're left off the board.
+++

## Groups
A group shares posts with just its members, like a club. Tap **New group** and name it. Only people you invite can join.

## Live sessions
Train together on one timer.

1. Load a workout in **Coach**.
2. Tap **Go live** under **Share this session** and choose who can join.
3. Share the six-character code, or invite someone by link.

When a mate goes live, a banner shows and their session appears under **Live now**. Tap **Join**.
`,
  },
  {
    id: "devices",
    area: "devices",
    title: "Heart rate, bikes and rowers",
    summary: "Connect a heart rate monitor or a smart bike or rower in your browser.",
    related: ["khronos-device", "coach-basics", "android-app"],
    body: `
Pair a heart rate strap, smart bike or rower over Bluetooth, straight from the browser. Khronos reconnects it by itself after that.

## What you can connect
Standard heart rate straps and watches, and smart indoor bikes and rowers (FTMS machines, such as a Concept2 PM5).

## Connecting
1. Go to **Settings › Devices** and tap **Add a device**.
2. Tap **Bike or rower** or **Heart-rate strap**.
3. Pick it in the list.

Or tap **Connect equipment** under a loaded session on the Coach.

+++ More detail
Khronos tries to reconnect when you open the app, when a workout starts, and every 15 seconds during one. Switch your strap or bike on late and it joins within seconds.

Tap anything you've paired to see its reading, name it ("C2 Rower"), choose which machine feeds your workout, reconnect it or forget it.
+++

## Chrome on Android
The **Khronos Android app** remembers your gear and keeps recording heart rate when you leave the screen. See **The Khronos Android app** in Help.

In Chrome itself, your gear is forgotten when Chrome closes unless you turn on two settings:

1. Open **chrome://flags**.
2. Turn on **Experimental Web Platform features** and **Web Bluetooth new permissions backend**.
3. Relaunch Chrome and reconnect once.

## What you'll see
Heart rate under the ring, and power with cadence or stroke rate. With a bike or rower, swipe across (or tap **Bike**) for its dashboard and chart. Pause there and a bar gives you **Resume**, and **⋯** for Go live, Edit session and Stop workout. Your heart rate and power are saved with the session.

## Heart-rate zones
Sessions with heart rate show time in five zones, worked out from your **heart-rate reserve** (max minus resting). Zone 2 is 60 to 70% of it.

- Set your heart rates in **Profile › Body & heart › Heart rate & zones**. Your zones show under them and change as you do.
- Hold a higher rate than your max for 5 seconds and Khronos raises your max for you, and says so.

+++ More detail
Until you set them, your max is estimated from your age and your resting rate is taken as 60. The **Last 7 days** card on Activity counts your real Zone 2 minutes.
+++

## VO2max
Khronos estimates your VO2max from a 2k row, a steady ride with power, or your air bike. See it, where each figure comes from, and how it's changing, in **Profile › Your numbers › VO2max**.

1. Add your **Body weight** in Profile › Body & heart.
2. Log a 2k row as a benchmark, or ride a bike with power steadily for at least 10 minutes.

+++ More detail
Each ride averages every steady 10 minutes in it: the oxygen your watts cost (the ACSM cycling equation), scaled up by how close your heart rate was to your max. Easy spins under half your heart-rate reserve, surging efforts and rowing don't count.

One ride is within about 10 to 15%, so the big number is the middle of your last five rides from the past eight weeks. It's marked rough until you set your max heart rate.

Air bikes, like a Rogue Echo or Assault bike, have no published equation: your arms work too and their watts are estimated. So Khronos calibrates yours to your rowing 2k. A steady air-bike ride within four weeks of a 2k sets what a watt costs you, and later rides use that.

Khronos spots air bikes by name; tap the bike in **Settings › Devices** to change it. Tap **×** on an older estimate to remove it or mark it **Air bike**.
+++

## Rowing figures
- **Rowing VO2max:** Concept2's estimate from your latest 2k. Add the 2k as a benchmark; it shows under **Profile › Your numbers › VO2max**, once your sex is set under **Profile › You › Sex**.
- **Each rowing session** in Activity shows metres per stroke, watts per beat and drift.
- **Watts per beat** rises as you get fitter.
- **Drift** is how much it fell over a steady piece of 20 minutes or more. Under 5% is a good aerobic sign.

## If it won't connect
Bluetooth in the browser needs Chrome or Edge on Android, Windows, macOS or ChromeOS; iPhone browsers can't. Check the device isn't connected to another app.
`,
  },
  {
    id: "khronos-device",
    area: "devices",
    title: "Khronos display and live sessions",
    summary: "Send a session to a Khronos Timer, share it with mates, or go live.",
    related: ["devices", "social"],
    body: `
Send a loaded session to your Khronos Timer and use your phone as the remote.

## Send to a device
1. In **Settings › Devices**, tap **Add a device**, then **Khronos Timer**, and enter the code on its screen.
2. On the Coach, tap **Send to a Khronos Timer** under **Share this session**.

Tap a linked timer in Settings to rename it, update it, pair its strap or change its settings.

+++ More detail
While the timer is following, the label above the Coach shows where sound plays: **App and timer**, **This app** or **Khronos Timer**. Tap it to change. Your usual choice is in **Settings › Sounds**.

The squares at the bottom of the timer count the two levels that change most: say rounds on the left and Tabata sets on the right.
+++

## Share a timer with mates
Let a mate send their own sessions to your timer.

1. Open **Settings › Devices** and tap your timer.
2. Tap **Share with a mate**, pick them, and choose when it ends.

- **First in keeps it** while a session runs.
- **You can take it back** by sending from your own Coach.
- **It stays yours.** Tap the cross beside a mate to stop sharing.

## Go live
**Go live** under **Share this session** shares a session in real time. Choose **Mates**, **Anyone** or **Invite only** to train together, or **Watch only** to let people watch. See **Mates, groups and live sessions**.

## Live with your timer
Train live with a mate and keep your own timer on the session.

- **Live first:** tap **Send to device** in the lobby, or on the pause screen once it's running.
- **Timer first:** tap **Go live with mates** before you start.

Each of you can put the session on your own timer.
`,
  },
  {
    id: "ai-options",
    area: "account",
    title: "The AI behind your sessions",
    summary: "Which AI builds your sessions, what it costs, and sharing credits with mates.",
    related: ["build-a-session"],
    body: `
An AI model writes each session; Khronos checks it against its own rules before you see it. Change the AI in **Settings › AI engine**.

![The AI engine sheet](/help/ai-engine.webp)

## The default
Khronos starts on Ollama, an AI hosted alongside the Khronos server, so there's nothing to set up. If sessions won't build, choose another provider.

## Using another AI
1. In **Settings › AI engine**, tap **Provider**.
2. Pick one, such as Google Gemini, OpenAI, Anthropic or OpenRouter.
3. Paste your API key from that provider, then close the sheet to save.

+++ More detail
If the new provider's key is missing or wrong, Khronos keeps using the last one that worked. Keys are stored with your account (or on this device if you're not signed in) and used only to build your sessions. The model, thinking level and **Refine plans** are under **Advanced options** or **More options**.
+++

## Switching for one session
The line under **Generate session** shows the AI in use. Tap it to switch for this session or from now on. In Basic, it's under **More options**.

## Cost
With your own key, the provider bills you. A session typically costs a fraction of a cent to a few cents.

## Your AI usage
**Settings › AI engine › Usage** shows this month's spend, the last 30 days, cost by model and each request. Tap **Monthly budget** to set a limit; you're told at 80% and 100%.

## Costs in Australian dollars
Costs show in A$, converted at the European Central Bank's daily rate. Change it in **Show costs in**.

+++ More detail
Providers bill in US$. If the rate can't be fetched, costs show in US$ until it can. Your bank may charge a slightly different amount.
+++

## Share your AI credits
Let a mate build sessions on your key. Your key stays on the server; they never see it.

1. In **Settings › AI engine**, tap **Shared credits**, then **Share with a mate**.
2. Choose the **Mate**, and check the **Spending limit**, **Ends** and **Models**.
3. Tap **Share with** your mate.

+++ More detail
- Tap a share to see what's left, its **Activity**, change limits, or **Stop sharing**.
- You see the days they used it and the cost, not what they wrote.
- You're both told at 80% and when it runs out.
- The last session before the limit can go over by a few cents.
- To top up, tap the share under **Ended** and raise the limit or extend it.
+++

## Invite someone with credits
Give a friend a little credit to try Khronos.

1. In **Shared credits**, tap **Invite someone with credits**.
2. Check **Each person gets**, **Lasts** and **Up to**. To keep costs down, set **Models** to only the cheaper ones.
3. Tap **Make the link**, then share it or show the QR code.

+++ More detail
The most it can cost you is shown first; unused credit is never charged. Each person's credit runs from when they claim it. Your friend signs in with Google to claim it, which is free. Claiming makes you mates. You're told when someone claims and when they write their first session.
+++

## Using a mate's credits
1. Open **Settings › AI engine** and tap **Provider**.
2. Pick their credits under **Shared with you**.

Credits from an invite link switch on when you claim them. Running low? Tap **Ask for more**.
`,
  },
  {
    id: "account-sync",
    area: "account",
    title: "Your account and your data",
    summary: "Signing in, your profile, settings, benchmarks and curves.",
    related: ["how-khronos-works", "app-updates"],
    body: `
Sign in with Google from the menu under your profile picture to keep everything in sync across devices.

## Without an account
Everything stays on this device. Clearing your browser data removes it.

## With an account
Your profile, history, programmes, recipes and settings sync. Anything from before you signed in is merged in.

## Editing your profile
**Profile** is everything that shapes your sessions:

- **You:** age, level, goal, days a week, injuries, units.
- **Body & heart:** heart rate and zones, body weight, your strap.
- **Sessions:** length, warm-up, pace and rest, changeovers, preferences, recipes.
- **Your numbers:** VO2max and your **Benchmarks**.
- **Where you train:** your places and equipment.

Tap a row to change it; it saves when you close the sheet.

## Settings
![Settings in Basic](/help/settings.webp)

**Settings** is how the app behaves: the Coach, sound and phone, your devices (Khronos Timer and Bluetooth gear), the AI, and mates and apps. In Basic, some rows are tucked away: tap **More options**, or turn on **Advanced options** in the menu under your profile picture.

## Benchmarks
Log what you actually did and how hard it felt; Khronos works out a figure the AI can use. No need to know your one-rep max.

| You log | You get |
|---|---|
| 100 kg for 5 reps | an estimated 1RM |
| 2000 m in 7:30 on the rower | your pace |
| 20 cal in 0:20 on the bike | calories a minute |

Under **Reps left in the tank**, say how many more you had (**Failure** if none).

+++ More detail
With several results for the same thing, Khronos combines them under **Combined**:

- **Two or more timed efforts** on one machine (2 to 30 minutes) give your **threshold pace**.
- **Several results for a lift** give one **combined 1RM**, recent and harder sets counting more.
- **A tested max plus a hard set of 3+ reps** fits how your reps fall off, so your 1RM is read from your own curve.

The AI prescribes from these, so "75% of 1RM" becomes a real weight. Results over six months old are marked possibly out of date.
+++

## Your curves
Open **Profile › Benchmarks** and tap an exercise under **Your curves**.

- **A lift:** the weight you could lift for 1 to 20 reps. Set **On the bar** to see your likely reps.
- **A machine:** your pace against time, with threshold and best sprint.
- **Anything else:** each result by date once you've logged it twice.

+++ More detail
A lift's curve is fitted to you when you have a tested max and a hard set of 3 or more reps; otherwise it follows the standard chart. A machine curve only answers between your shortest and longest efforts. Tap any result under **Results** to change it.
+++
`,
  },
  {
    id: "android-app",
    area: "account",
    title: "The Khronos Android app",
    summary: "Download the app so heart rate keeps recording when you leave the screen.",
    related: ["devices", "app-updates"],
    body: `
On an Android phone, the Khronos app keeps your workout going when you switch apps or take a call: the timer, its sounds and your heart rate. It reconnects your gear by itself too.

## Download
**[Download the Khronos app](/android/khronos.apk)** on your phone. It needs Android 7 or later. iPhones can't install it.

## Installing
1. Tap **Download the Khronos app** above. When Chrome says it's done, tap **Open**.
2. If Android says it can't install unknown apps, tap **Settings**, turn on **Allow from this source**, then go back.
3. Tap **Install**. If Play Protect asks about it, tap **More details**, then **Install anyway**.
4. Open **Khronos** and sign in with Google from the menu under the profile picture.

## The first time you connect gear
- Allow **Nearby devices**, so Khronos can find your strap, bike or rower.
- Allow **Notifications**, so the workout timer and **Recording heart rate** can show there.

## In the background
Switch apps, take a call or lock the screen and the Coach carries on:

- Its sounds play on time, from your sound set.
- A media card in the notifications and on the lock screen shows the step, the time left and your heart rate, with pause, skip and mute.
- Your finish-by alarm still goes off.

The first time you start a workout, Android asks whether Khronos may always run in the background. Tap **Allow**, so the phone doesn't stop it mid-session.

## Updates
Everything you see updates by itself, as on the website. When the app itself changes, download it again and tap **Install** over the top. You stay signed in.

+++ More detail
The app isn't on Google Play yet, which is why Android asks about unknown apps. It opens khronos.cotlone.com in its own window, with your account and history as on the web.

**App not installed** usually means an early test version is on the phone. Uninstall it, then install again.
+++
`,
  },
  {
    id: "app-updates",
    area: "account",
    title: "Installing and updating the app",
    summary: "Add Khronos to your home screen and keep it up to date.",
    related: ["account-sync", "android-app"],
    body: `
Install Khronos like an app so it opens full screen and keeps working offline for loaded sessions.

## Installing
In Chrome, open the browser menu and choose **Install app** or **Add to Home screen**.

On Android you can also install the full Khronos app, which keeps recording heart rate in the background. See **The Khronos Android app**.

## Updates
Khronos updates itself when you open it. If you're in the middle of something, you'll see **New version available** with **Reload**. Updates never interrupt a workout. **What's new** in the menu lists every change. At the bottom, **This install** shows the versions you're running: the website, the Android app (in the app) and your timer's firmware, with whether a newer one is out.
`,
  },
];
